/**
 * Adyen Web (Card Component) utilities — advanced flow, lazy-loaded.
 * Mirrors utils/squarePayment.js ergonomics. The @adyen/adyen-web package and its
 * CSS are imported dynamically (D7) so non-adyen venues never download the chunk.
 */

/**
 * Pre-warm the Adyen SDK + CSS chunks so the card mounts instantly later. Fire-and-forget;
 * call early (payment-page mounted) ONLY for adyen venues so non-adyen venues keep D7 (never
 * download the chunk). Safe to call repeatedly — webpack caches the resolved chunk.
 * @returns {Promise<void>}
 */
export function preloadAdyenSdk () {
  return Promise.all([import('@adyen/adyen-web'), import('./adyenLib')]).catch(() => {})
}

/**
 * Initialize an AdyenCheckout instance. Lazy-imports the SDK + CSS on first call.
 * @param {Object} cfg { clientKey, environment, amount:{currency,value(minor)}, countryCode,
 *                        onSubmit, onAdditionalDetails, onError }
 * @returns {Promise<Object>} AdyenCheckout instance
 */
export async function initializeAdyenCheckout (cfg) {
  // SDK (dynamic import — keeps the big SDK chunk lazy, D7) loaded in parallel with its
  // stylesheet chunk. The CSS is pulled via a tiny JS module (adyenLib.js) that statically
  // imports the stylesheets, because a bare CSS-only dynamic import — await import('....css')
  // — is dropped by this webpack 4 build (that is why the form rendered unstyled). adyenLib
  // deliberately does NOT static-import the SDK: that pulls the SDK through babel and injects
  // a core-js polyfill this build cannot resolve.
  const [{ default: AdyenCheckout }] = await Promise.all([
    import('@adyen/adyen-web'),
    import('./adyenLib')
  ])
  return AdyenCheckout({
    clientKey: cfg.clientKey,
    environment: cfg.environment, // 'test' | 'live'
    amount: cfg.amount, // { currency, value } in minor units
    countryCode: cfg.countryCode,
    onSubmit: cfg.onSubmit, // (state) => POST state.data to backend (/payments)
    onAdditionalDetails: cfg.onAdditionalDetails, // (state) => POST state.data (/payments/details)
    onError: cfg.onError
  })
}

/**
 * Mount a standalone Card Component (the Square card.attach analog).
 * showPayButton:false — the existing unified bottom CTA drives submit via card.submit() (D1).
 * @returns {Object} the card component
 */
export function bindAdyenCard (checkout, containerSelector, options = {}) {
  const card = checkout.create('card', {
    showPayButton: false,
    hasHolderName: false,
    enableStoreDetails: true,
    styles: options.styles || {},
    ...options
  })
  card.mount(containerSelector)
  return card
}

/**
 * Mount an Adyen wallet Component (Apple Pay / Google Pay). showPayButton:false — we do NOT
 * render Adyen's own wallet button; the unified bottom CTA triggers component.submit() (same as
 * the card). Mounts only if isAvailable() resolves (unsupported device/browser, or incomplete
 * Apple/Google setup, rejects → caller skips this wallet silently).
 * @param {Object} checkout AdyenCheckout instance (the same one the card uses)
 * @param {'applepay'|'googlepay'} type
 * @param {string} containerSelector e.g. '#card-payment-adyen-applepay'
 * @param {Object} cfg { amount:{currency,value(minor)}, configuration:{merchantName, merchantId?, gatewayMerchantId} }
 * @returns {Promise<Object>} the mounted component (throws if unavailable)
 */
export async function bindAdyenWallet (checkout, type, containerSelector, cfg = {}) {
  const component = checkout.create(type, {
    showPayButton: false, // unified bottom CTA drives submit (clickButton → component.submit())
    ...cfg
  })
  await component.isAvailable() // rejects on unsupported/incomplete setup
  component.mount(containerSelector)
  return component
}
